'use client';

import { FC, ReactNode, Suspense, useState } from 'react';

import { GetStartedCarTile } from 'src/checkout/components/GetStartedCarTile/GetStartedCarTile';
import { GetStartedChatButton } from 'src/checkout/components/GetStartedChatButton/GetStartedChatButton';
import { GetStartedForm } from 'src/checkout/components/GetStartedForm/GetStartedForm';
import { productDetailPayloadToBaseCarData } from 'src/data/ProductApi/ProductDetailPayloadToBaseCarData';
import { BasicModal } from 'src/general/components/BasicModal/BasicModal';
import { Button } from 'src/general/components/Button/Button';
import { ProductDetailsPayload } from 'src/types/ProductDetail.type';

import classNames from 'classnames';
import GetStartedCarDetails from 'src/checkout/components/GetStartedCarTile/GetStartedCarDetails';
import { CAR_TRANSFER_FEE_NSW, PLATE_TRANSFER_FEE } from 'src/constants';
import { ModalGovernmentCharges } from 'src/general/components/ModalGovernmentCharges/ModalGovernmentCharges';
import { useModal } from 'src/hooks/useModal';
import styles from './GetStarted.module.scss';
import GetStartedHeadings from './GetStartedHeadings';

export interface GetStartedMicrocopyProps {
  [key: string]: ReactNode;
  [key: number]: ReactNode;
}

interface GetStartedProps {
  microcopy: GetStartedMicrocopyProps;
  productDetails: ProductDetailsPayload;
}

export const GetStarted: FC<GetStartedProps> = ({ microcopy, productDetails }) => {
  const carData = productDetailPayloadToBaseCarData(productDetails);
  const [isOpenViewTermsModal, setIsOpenViewTermsModal] = useState<boolean>(false);
  const {
    header,
    description,
    viewTerms,
    termModalTitle,
    termModalContent,
    governmentChargesNote,
    governmentChargesTitle,
  } = microcopy;
  const { isVisible, open, close } = useModal();

  const handleOpenViewTermsModal = () => {
    setIsOpenViewTermsModal(true);
  };

  const handleCloseViewTermsModal = () => {
    setIsOpenViewTermsModal(false);
  };

  return (
    <>
      <div className={styles.contentContainer}>
        <div className={styles.mobileOnly}>
          <GetStartedHeadings header={header} description={description} />
        </div>

        <div className={classNames(styles.mobileOnly, styles.carDetailsContainer)}>
          <GetStartedCarDetails {...carData} />
        </div>

        <div className={styles.gridContainer}>
          <div className={styles.desktopOnly}>
            <GetStartedCarTile onOpen={open} carData={carData} microcopy={microcopy} />
            <div className={styles.viewTermsButtonContainer}>
              <Button variant="link" className={styles.viewTermsButton} size="small" onClick={handleOpenViewTermsModal}>
                {viewTerms}
              </Button>
            </div>
          </div>

          <div>
            <div className={styles.desktopOnly}>
              <GetStartedHeadings header={header} description={description} />
            </div>
            <Suspense>
              <GetStartedForm carData={carData} microcopy={microcopy} />
            </Suspense>
          </div>
        </div>

        <div className={classNames(styles.viewTermsButtonContainer, styles.mobileOnly)}>
          <Button variant="link" className={styles.viewTermsButton} size="small" onClick={handleOpenViewTermsModal}>
            {viewTerms}
          </Button>
        </div>
      </div>

      <GetStartedChatButton microcopy={microcopy} />

      <BasicModal
        id="view-terms-modal"
        isOpen={isOpenViewTermsModal}
        onClose={handleCloseViewTermsModal}
        title={termModalTitle}
      >
        <div className={styles.termsModalContent}>{termModalContent}</div>
      </BasicModal>

      <ModalGovernmentCharges
        isOpen={isVisible}
        header={governmentChargesTitle}
        description={governmentChargesNote}
        onClose={close}
        carPrice={carData.price}
        dutyPrice={carData.dutyPrice}
        transferFee={CAR_TRANSFER_FEE_NSW}
        plateTransferFee={PLATE_TRANSFER_FEE}
      />
    </>
  );
};
